import {
  Box,
  Center,
  Circle,
  Grid,
  HStack,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { Link } from 'react-router'
import { CheckDone } from 'src/components/icon'
import { formatDate } from 'src/utils/formatDate'

type Props = {
  order: number
  /** 文書閲覧ページのpath */
  docPath: string
  docName: string
  docVersion: string
  patientBrowsedAt: string | undefined
}

export const DocumentBanner: React.FC<Props> = ({
  order,
  docPath,
  docName,
  docVersion,
  patientBrowsedAt,
}) => {
  const circleSize = useBreakpointValue({ base: '6', lg: '10' })
  const supplementaryInfoFontSize = useBreakpointValue({
    base: '2xs',
    lg: 'sm',
  })
  const isLargerThanSm = useBreakpointValue({ base: false, sm: true })
  const isLargerThanLg = useBreakpointValue({ base: false, lg: true })

  return (
    <Box as="section" aria-label={docName}>
      <LinkBox
        as={Grid}
        alignItems="center"
        justifyContent="space-between"
        templateColumns="1fr max-content"
        gap={4}
        border="2px solid"
        borderRadius="lg"
        borderColor="green.600"
        bg="white"
        w="full"
        p={{ base: '2', md: '4' }}
        _hover={{ bg: 'green.50' }}
        _active={{ bg: 'green.100' }}
      >
        <Grid
          overflow="hidden"
          rowGap={2}
          columnGap={4}
          templateColumns={{
            base: 'minmax(0, 1fr)',
            lg: 'minmax(0, 1fr) max-content',
          }}
          maxW="full"
        >
          <HStack
            align="center"
            spacing={{ base: '2', lg: '4' }}
            flex="1"
            w="full"
          >
            {!!patientBrowsedAt ? (
              <Circle bg="green.600" color="white" size={circleSize}>
                <CheckDone size={isLargerThanLg ? 'lg' : 'md'} />
              </Circle>
            ) : (
              <Circle
                as="span"
                size={circleSize}
                border="2px solid"
                borderColor="green.600"
                color="green.600"
                fontWeight="bold"
                fontSize="xl"
              >
                {order}
              </Circle>
            )}

            <Box overflow="hidden">
              <LinkOverlay
                as={Link}
                display="block"
                to={docPath}
                fontSize={{ base: 'md', md: 'lg', lg: '2xl' }}
                fontWeight="bold"
                color="green.600"
                w="full"
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
              >
                {docName}
              </LinkOverlay>
            </Box>
          </HStack>

          {!!patientBrowsedAt && (
            <HStack
              align="center"
              justify={{ base: 'start', lg: 'end' }}
              spacing={{ base: '4', lg: '12' }}
              wordBreak="keep-all"
            >
              <Stack spacing="1">
                {isLargerThanSm && (
                  <HStack spacing={{ base: '1', md: '4' }}>
                    <Text
                      fontSize={supplementaryInfoFontSize}
                      fontWeight="bold"
                      as="span"
                    >
                      閲覧時刻
                    </Text>
                    <Text
                      fontSize={supplementaryInfoFontSize}
                      wordBreak="keep-all"
                      as="time"
                      dateTime={patientBrowsedAt}
                    >
                      {formatDate(patientBrowsedAt, 'YYYY/MM/DD (ddd) HH:mm')}
                    </Text>
                  </HStack>
                )}
                <HStack
                  spacing={{ base: '1', md: '4' }}
                  display={{ base: 'none', sm: 'flex' }}
                >
                  <Text
                    fontSize={supplementaryInfoFontSize}
                    fontWeight="bold"
                    as="span"
                  >
                    閲覧状況
                  </Text>
                  <HStack>
                    <Text fontSize={supplementaryInfoFontSize} as="span">
                      患者
                    </Text>
                    <Center
                      as="span"
                      borderRadius="full"
                      bg="green.600"
                      h="4"
                      w="4"
                      color="white"
                    >
                      <CheckDone size="sm" />
                    </Center>
                  </HStack>
                </HStack>
              </Stack>
            </HStack>
          )}
        </Grid>

        <Text
          display={{ base: 'none', sm: 'block' }}
          fontSize={supplementaryInfoFontSize}
          wordBreak="keep-all"
          as="span"
        >{`${docVersion}版`}</Text>
      </LinkBox>
    </Box>
  )
}
